import { createContext, useContext, useId, useState } from "react";
import type { PlanStep } from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import type { EmailDisplay } from "../src/email-display.ts";
import { answersMessageIdFor, draftFreshness } from "../src/inbox-view.ts";
import type { LateReplyView } from "../src/late-reply-view.ts";
import {
  foldsWhenLong,
  lateReplyNote,
  paragraphsOf,
  type RepliedMessageView,
  repliedMessageView,
  sourceMessages,
  writtenText,
} from "../src/replied-message-view.ts";

type Shown = { id: string; from: string; at: string };

/** "Ines Hartmann <ines@…>" reads as the name; a bare address stays an address. */
function senderName(from: string): string {
  return (
    from
      .replace(/\s*<[^>]*>\s*$/, "")
      .replace(/^"(.*)"$/, "$1")
      .trim() || from
  );
}

/**
 * The thread on screen below the card: its lead's first name and the address they wrote from, and the
 * id of its newest message as the inbox shows it, which loads apart from the reply agent's record.
 */
export type ThreadLead = { name: string | null; address: string | null; newestId: string | null };

/**
 * The thread on screen just below the card, as on the unified inbox's timeline, newest first
 * (LEA-514). There the quote of the thread's newest message folds to one line pointing down at it
 * (Dawid's picker, ledger `c05ce66c`); an older message keeps its full quote. Null on the reply
 * agent's own page, which keeps every quote in full.
 */
export const ThreadBelow = createContext<ThreadLead | null>(null);

/**
 * "Ines Hartmann <ines@…>" reads as "Ines". A bare address reads as the lead's first name where it is
 * the lead's own address, and stays an address otherwise.
 */
function firstName(from: string, lead: ThreadLead): string {
  const name = senderName(from);
  if (!name.includes("@")) return name.split(/\s+/u)[0] ?? name;
  const address = lead.address?.toLowerCase();
  return lead.name && address && from.toLowerCase().includes(address) ? lead.name : name;
}

/**
 * The folded quote: the message is the one just below, so the card names it and points down. Where
 * the draft's record of what it answers has not loaded, it claims nothing, as the full quote does.
 */
function RepliedBelow({
  view,
  lead,
  late,
  when,
}: {
  view: Exclude<RepliedMessageView<Shown>, { kind: "none" }>;
  lead: ThreadLead;
  late: string | null;
  when: (at: string) => string;
}) {
  const { message } = view;
  const name = firstName(message.from, lead);
  const lede = view.kind === "answers" ? "Replying to" : "Latest from";
  const tail = view.kind === "answers" ? "'s message below" : ", the message below";
  return (
    <section
      className="lcr-replied"
      data-replied="below"
      aria-label={`${lede} ${name}${tail}, ${when(message.at)}`}
    >
      <p className="lcr-replied-head">
        <span className="lcr-replied-lede">{lede}</span>{" "}
        <a className="lcr-replied-below" href={`#message-${message.id}`}>
          <strong>{name}</strong>
          {tail}
        </a>{" "}
        <span aria-hidden="true">·</span> <time dateTime={message.at}>{when(message.at)}</time>
      </p>
      {late && <p className="lcr-replied-late">{late}</p>}
    </section>
  );
}

/**
 * The message a draft answers, shown above the draft so a rep reads what they are replying to
 * without leaving it (docs/2026-10-05-review-reply-header.md). It replaces the "Source email"
 * links and the line that said which message the draft answered: the message is the answer to
 * both. A long message opens folded and unfolds in place; the rest of the thread is one link away.
 */
export function RepliedMessage({
  view,
  text,
  late,
  when,
}: {
  view: Exclude<RepliedMessageView<Shown>, { kind: "none" }>;
  /** What the sender wrote, quoted history left out (`writtenText`). */
  text: string;
  /** What a held reply changes for the rep, or null (`lateReplyNote`). */
  late: string | null;
  when: (at: string) => string;
}) {
  const bodyId = useId();
  const [open, setOpen] = useState(false);
  const { message } = view;
  const newer = view.kind === "answers" ? view.newer : null;
  const name = senderName(message.from);
  const lede =
    view.kind === "latest" ? "Latest from" : newer ? "This draft answers" : "Replying to";
  const folds = foldsWhenLong(text);
  const paragraphs = paragraphsOf(text);
  return (
    <section
      className="lcr-replied"
      data-replied={newer ? "stale" : view.kind}
      aria-label={`${lede} ${name}, ${when(message.at)}`}
    >
      {newer && (
        <p className="lcr-fresh" data-fresh="stale" role="status">
          <a href={`#message-${newer.id}`}>
            {senderName(newer.from)} wrote again {when(newer.at)}
          </a>
          ; this draft answers the earlier message below. Approve stays off until the newer one is
          drafted.
        </p>
      )}
      <p className="lcr-replied-head">
        <span className="lcr-replied-lede">{lede}</span> <strong>{name}</strong>{" "}
        <time dateTime={message.at}>{when(message.at)}</time>
      </p>
      {late && <p className="lcr-replied-late">{late}</p>}
      <div className="lcr-replied-body" id={bodyId} data-folded={folds && !open ? "" : undefined}>
        {paragraphs.length === 0 ? (
          <p className="lcr-replied-line lcr-replied-empty">This message has no text.</p>
        ) : (
          paragraphs.map((lines, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a message's paragraphs never reorder.
            <p className="lcr-replied-line" key={index}>
              {lines.map((line, at) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: nor do a paragraph's lines.
                <span key={at}>
                  {at > 0 && <br />}
                  {line}
                </span>
              ))}
            </p>
          ))
        )}
      </div>
      <p className="lcr-replied-foot">
        {folds && (
          <button
            type="button"
            className="lcr-linklike"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen(!open)}
          >
            {open ? "Show less" : "Show the whole message"}
          </button>
        )}
        <a className="lcr-linklike" href={`#message-${message.id}`}>
          Open in the thread
        </a>
      </p>
    </section>
  );
}

/**
 * Other messages a step drew on, each link named by who wrote it and when. One noun repeated on
 * every link told a rep nothing about which message it opened.
 */
export function SourceMessageLinks({
  lead,
  messages,
  when,
}: {
  /** What the links are, in a few words: "Also draws on", "From". */
  lead: string;
  messages: readonly Shown[];
  when: (at: string) => string;
}) {
  if (messages.length === 0) return null;
  return (
    <p className="lcr-step-source">
      <span>{lead}</span>
      {messages.map((message) => (
        <a className="lcr-source-chip" key={message.id} href={`#message-${message.id}`}>
          {senderName(message.from)}, {when(message.at)}
        </a>
      ))}
    </p>
  );
}

type ThreadMessage = Shown & { direction: "inbound" | "outbound"; plainText: string };

/*
 * What sits between a step's title and its body. A reply to review shows the message it answers,
 * in full, so the rep reads it beside the draft; a stale draft keeps its text on screen, since the
 * rep may reuse it, but Approve is off until the newer message is drafted. Every other message a
 * step drew on is a link named by sender and time.
 */
export function StepHeader({
  step,
  messages,
  displays,
  events,
  late,
  when,
  parse,
  now,
}: {
  step: Pick<PlanStep, "input" | "state" | "sources">;
  /** The thread the page already holds; empty until it is imported. */
  messages: readonly ThreadMessage[];
  /** One per message, in order: the thread pane's own preparation of each email. */
  displays: readonly EmailDisplay[];
  /** Null while the event record loads: nothing is claimed about the draft until it has. */
  events: readonly { kind: string; detail: object }[] | null;
  late: LateReplyView | null;
  when: (at: string) => string;
  parse: (html: string) => Document;
  now: Date;
}) {
  const reviewing =
    step.input.kind === "reply" && ["pending", "invalidated"].includes(step.state)
      ? step.input
      : null;
  const view: RepliedMessageView<ThreadMessage> = reviewing
    ? repliedMessageView(
        events
          ? draftFreshness(answersMessageIdFor(events, reviewing.draftRevision), messages)
          : { kind: "unknown" },
        messages,
      )
    : { kind: "none" };
  const shown = view.kind === "none" ? null : view.message;
  const lateNote = late ? lateReplyNote(late, now) : null;
  // A stale draft answers an older message, so it never folds; nor does one whose message is not the
  // newest on screen, though the agent's record has not yet seen the newer one.
  const lead = useContext(ThreadBelow);
  const below =
    lead !== null &&
    shown !== null &&
    messages.at(-1)?.id === shown.id &&
    lead.newestId === shown.id;
  return (
    <>
      {view.kind !== "none" && lead && below && (
        <RepliedBelow view={view} lead={lead} late={lateNote} when={when} />
      )}
      {view.kind !== "none" && !below && (
        <RepliedMessage
          view={view}
          text={writtenText(
            displays[messages.indexOf(view.message)],
            view.message.plainText,
            parse,
          )}
          late={lateNote}
          when={when}
        />
      )}
      <SourceMessageLinks
        lead={shown ? "Also draws on" : "From"}
        messages={sourceMessages(step.sources, messages, shown?.id ?? null)}
        when={when}
      />
    </>
  );
}
